import ModKeyChar from "@/components/ModKeyChar";
import { Copy } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CopySelectionStore } from "../context/CopySelectionContext";

interface UseDataTableContextMenuOptions {
  store: CopySelectionStore;
  copySelectionToClipboard: () => void;
}

export function useDataTableContextMenu({
  store,
  copySelectionToClipboard,
}: UseDataTableContextMenuOptions) {
  const { t } = useTranslation();

  const [contextMenuPos, setContextMenuPos] = useState<{
    left: number;
    top: number;
  }>();

  const contextMenuRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (store.hasSelection()) {
        e.preventDefault();
        setContextMenuPos({ left: e.clientX, top: e.clientY });
      }
    },
    [store],
  );

  // Close context menu when selection is cleared
  useEffect(() => {
    if (!contextMenuPos) return;
    return store.subscribe(() => {
      if (!store.hasSelection()) {
        setContextMenuPos(undefined);
      }
    });
  }, [contextMenuPos, store]);

  // Close context menu on outside click, escape, or scroll
  useEffect(() => {
    if (!contextMenuPos) {
      return;
    }

    const close = (event: MouseEvent) => {
      if (contextMenuRef.current?.contains(event.target as Node)) {
        return;
      }
      setContextMenuPos(undefined);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "escape") {
        setContextMenuPos(undefined);
      }
    };

    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("scroll", close, true);

    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("scroll", close, true);
    };
  }, [contextMenuPos]);

  const contextMenu =
    contextMenuPos &&
    createPortal(
      <div
        ref={contextMenuRef}
        data-prevent-dialog-close
        className="pointer-events-auto fixed z-[100] min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
        style={contextMenuPos}
      >
        <button
          className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
          onMouseDown={(e) => {
            e.stopPropagation();
            copySelectionToClipboard();
            setContextMenuPos(undefined);
          }}
        >
          <Copy className="h-4 w-4" />
          {t("table.contextMenu.copy")}
          <span className="ml-auto text-xs tracking-widest text-muted-foreground">
            <ModKeyChar />C
          </span>
        </button>
      </div>,
      document.body,
    );

  return { handleContextMenu, contextMenu };
}
